<template>
  <div class="cabinet-root">
    <div class="d-flex align-items-start justify-content-between gap-2 mb-4 flex-wrap">
      <h1 class="mb-0">Личный кабинет</h1>
      <button
        v-if="!loading && guideUserId"
        type="button"
        class="btn btn-link btn-sm text-muted p-0"
        title="Как пользоваться"
        @click="openCabinetGuide"
      >
        <i class="fa-regular fa-circle-question me-1" aria-hidden="true" />
        Как пользоваться
      </button>
    </div>

    <div
      v-if="!loading && trialStatusBanner"
      class="alert mb-4"
      :class="trialStatusBanner.variant"
      role="status"
    >
      {{ trialStatusBanner.text }}
    </div>

    <div
      v-if="!loading && billingReturnBanner && showRetailBilling"
      class="alert mb-4"
      :class="billingReturnBanner.variant"
      role="status"
    >
      {{ billingReturnBanner.text }}
    </div>

    <div class="cabinet-layout">
      <aside class="cabinet-sidebar" aria-label="Разделы личного кабинета">
        <button
          v-for="tab in cabinetTabs"
          :key="tab.key"
          type="button"
          class="cabinet-tab"
          :class="{ active: activeTab === tab.key }"
          @click="selectCabinetTab(tab)"
        >
          {{ tab.label }}
        </button>
      </aside>

      <section class="cabinet-main">
        <div v-if="loading" class="cabinet-main__loading text-center my-5">
          <div class="spinner-border" role="status"></div>
        </div>

        <template v-else>
          <div v-if="activeTab === 'profile'" class="cabinet-section">
            <form class="card p-4 mb-4" @submit.prevent="saveProfile">
      <h2 class="h5">Персональные данные</h2>
      <div class="row g-3">
        <div class="col-md-6">
          <input v-model="profile.name" class="form-control" placeholder="ФИО" />
        </div>
        <div class="col-md-6">
          <EmailInput v-model="profile.email" class="form-control" placeholder="name@example.com" />
        </div>
        <div class="col-md-6">
          <input v-model="profile.organization_name" class="form-control" placeholder="Организация" />
        </div>
        <div class="col-md-6">
          <PhoneInput v-model="profile.phone" class="form-control" />
        </div>
        <div class="col-12 d-grid">
          <button class="btn btn-dark">Сохранить</button>
        </div>
      </div>
            </form>

            <div class="card p-4 mb-4">
              <h2 class="h5 mb-2">Смена пароля</h2>
              <p class="text-muted small mb-3">
                Отправим ссылку подтверждения на <strong>{{ profile.email || 'ваш e-mail' }}</strong>.
                После перехода по ссылке задайте новый пароль.
              </p>
              <p v-if="passwordChangeMessage" class="small mb-2" :class="passwordChangeError ? 'text-danger' : 'text-success'">
                {{ passwordChangeMessage }}
              </p>
              <button type="button" class="btn btn-dark btn-sm" :disabled="passwordChangeSending" @click="requestPasswordChange">
                <span v-if="passwordChangeSending" class="spinner-border spinner-border-sm me-1" />
                Отправить ссылку для смены пароля
              </button>
            </div>
          </div>

          <div v-else-if="activeTab === 'settings'" class="cabinet-section">
            <div class="card p-4 mb-4">
      <h2 class="h5 mb-2">Этапы канбана для новых списков</h2>
      <p class="text-muted small mb-3">
        Эти этапы копируются в каждый <strong>новый</strong> список. Существующие списки настраиваются на странице «Списки».
      </p>
      <div v-if="!kanbanTemplateStages.length" class="text-muted small mb-2">Шаблон пуст — будут использованы этапы по умолчанию на сервере.</div>
      <KanbanStagesEditor mode="template" :stages="kanbanTemplateStages" />
      <div class="d-flex flex-wrap gap-2 mt-2">
        <button type="button" class="btn btn-sm btn-dark" :disabled="kanbanSaving" @click="saveKanbanDefaults">
          <span v-if="kanbanSaving" class="spinner-border spinner-border-sm me-1" />
          Сохранить шаблон
        </button>
      </div>
      <p v-if="kanbanSaveMessage" class="small mt-2 mb-0" :class="kanbanSaveError ? 'text-danger' : 'text-success'">{{ kanbanSaveMessage }}</p>
            </div>

            <div class="card p-4 mb-4">
      <div class="mb-3">
        <h2 class="h5 mb-1">Настройки AI-анализа</h2>
        <p class="text-muted small mb-0">
          Выберите, какие функции доступны в карточке тендера. Счётчик: <strong>{{ pipelineEnabledLabel }}</strong>
        </p>
      </div>
      <PipelineSettingsList
        v-model="pipelineDraft"
        use-mvp-hints
        id-prefix="cabinet-pl"
        hints-footnote="Ориентировочные значения для планирования; фактический расход зависит от объёма документов и выбранных этапов."
      />
      <p v-if="pipelineSaveMessage" class="small mt-2 mb-2" :class="pipelineSaveError ? 'text-danger' : 'text-success'">
        {{ pipelineSaveMessage }}
      </p>
      <button type="button" class="btn btn-dark" :disabled="pipelineSaving" @click="savePipelineSettings">
        <span v-if="pipelineSaving" class="spinner-border spinner-border-sm me-1" />
        Сохранить настройки AI
      </button>
            </div>

            <div class="card p-4 mb-4">
      <h2 class="h5 mb-2">Уведомления на e-mail</h2>
      <p class="text-muted small mb-3">Письма отправляются с адреса службы AI Tenders.</p>
      <div class="form-check mb-2">
        <input id="notif-email-alerts" v-model="notifications.email_alerts" class="form-check-input" type="checkbox" />
        <label class="form-check-label" for="notif-email-alerts">Включить e-mail уведомления</label>
      </div>
      <div v-if="showRetailBilling" class="form-check mb-2 ms-3">
        <input
          id="notif-email-billing"
          v-model="notifications.email_billing"
          class="form-check-input"
          type="checkbox"
          :disabled="!notifications.email_alerts"
        />
        <label class="form-check-label" for="notif-email-billing">Подписка и платежи</label>
      </div>
      <div v-if="showRetailBilling" class="form-check mb-2 ms-3">
        <input
          id="notif-email-low"
          v-model="notifications.email_low_balance"
          class="form-check-input"
          type="checkbox"
          :disabled="!notifications.email_alerts"
        />
        <label class="form-check-label" for="notif-email-low">Низкий баланс токенов</label>
      </div>
      <div class="form-check mb-3 ms-3">
        <input
          id="notif-email-roadmap"
          v-model="notifications.email_roadmap_overdue"
          class="form-check-input"
          type="checkbox"
          :disabled="!notifications.email_alerts"
        />
        <label class="form-check-label" for="notif-email-roadmap">Просрочка дорожной карты</label>
      </div>
      <p v-if="notificationsSaveMessage" class="small mb-2" :class="notificationsSaveError ? 'text-danger' : 'text-success'">
        {{ notificationsSaveMessage }}
      </p>
      <button type="button" class="btn btn-dark btn-sm" :disabled="notificationsSaving" @click="saveNotifications">
        <span v-if="notificationsSaving" class="spinner-border spinner-border-sm me-1" />
        Сохранить уведомления
      </button>
            </div>
          </div>

          <div v-else-if="activeTab === 'integration'" class="cabinet-section">
            <div class="card p-4 mb-4">
      <h2 class="h5 mb-2">Интеграция с Tenderplan</h2>
      <p class="text-muted small mb-3">
        Подключите API-ключ вашего аккаунта Tenderplan, чтобы импортировать существующие поисковые ключи.
        Без ключа используется встроенный поиск сервиса.
      </p>
      <p v-if="tpIntegration.connected" class="small mb-2">
        Статус: <strong>подключено</strong>
        <span v-if="tpIntegration.token_hint"> ({{ tpIntegration.token_hint }})</span>
        <span v-if="tpIntegration.keys_count != null" class="text-muted"> · ключей: {{ tpIntegration.keys_count }}</span>
      </p>
      <p v-else class="small mb-2 text-muted">Статус: не подключено</p>
      <div v-if="tpIntegrationMessage" class="small mb-2" :class="tpIntegrationError ? 'text-danger' : 'text-success'">
        {{ tpIntegrationMessage }}
      </div>
      <label class="form-label" for="cabinet-tp-token">API-ключ</label>
      <input
        id="cabinet-tp-token"
        v-model="tpTokenInput"
        type="password"
        class="form-control mb-2"
        autocomplete="off"
        placeholder="Вставьте новый ключ для подключения или замены"
        :disabled="tpIntegrationLoading"
      />
      <div class="d-flex flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-dark btn-sm"
          :disabled="tpIntegrationLoading || !tpTokenInput.trim()"
          @click="saveTenderplanIntegration"
        >
          <span v-if="tpIntegrationLoading" class="spinner-border spinner-border-sm me-1" />
          {{ tpIntegration.connected ? 'Сохранить ключ' : 'Подключить' }}
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="tpIntegrationLoading || !tpIntegration.connected"
          @click="syncTenderplanKeys"
        >
          Синхронизировать ключи
        </button>
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          :disabled="tpIntegrationLoading || !tpIntegration.connected"
          @click="removeTenderplanIntegration"
        >
          Отключить
        </button>
      </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'catalog'" class="cabinet-section">
            <div class="row g-2 mb-4">
      <div class="col-md-6 d-grid">
        <button class="btn btn-dark" @click="globalItemsModalShown = true">
          📄 Список товаров и услуг
        </button>
      </div>
      <div class="col-md-6 d-grid">
        <button class="btn btn-outline-primary" @click="winCriteriaModalShown = true">
          🏆 Критерии победы
        </button>
      </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'archives'" class="cabinet-section">
            <div class="card p-4 mb-4">
      <h2 class="h5 mb-3">Списки тендеров</h2>

      <div v-if="archives.length === 0" class="text-muted">
        Списков пока нет
      </div>

      
      <div v-else class="table-responsive">
        <table class="table table-sm align-middle">
          <thead class="table-light">
            <tr>
              <th>Название</th>
              <th>Создан</th>
              <th class="text-end">Количество участий</th>
              <th class="text-end">Количество побед</th>
              <th class="text-end">Общая цена победных тендеров</th>
              <th class="text-end">Тендеры</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in archives" :key="a.id">
              <td>{{ a.name }}</td>
              <td>{{ formatDate(a.created_at) }}</td>
              <td class="text-end">{{ a.participated_cnt }}</td>
              <td class="text-end">{{ a.won_cnt }}</td>
              <td class="text-end">
                {{ a.won_total_sum ? money(Number(a.won_total_sum)) : money(0) }}
              </td>
              <td class="text-end">{{ a.items_cnt }}</td>
            </tr>
          </tbody>
        </table>
      </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'billing'" class="cabinet-section">
            <div class="card p-4 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3">
        <div>
          <h2 class="h5 mb-1">Баланс токенов</h2>
          <p class="display-6 mb-0">{{ tokenBalanceFormatted }}</p>
          <template v-if="isB2bWireBilling">
            <p class="small mb-0 mt-2"><strong>Оплата по договору</strong></p>
            <p v-if="wallet.subscription_renews_at" class="small text-muted mb-0 mt-1">
              Следующее продление: {{ formatDate(wallet.subscription_renews_at) }}
            </p>
            <p v-if="b2bMonthlyTokensDisplay != null" class="small text-muted mb-0 mt-1">
              {{ intFmt.format(b2bMonthlyTokensDisplay) }} токенов / мес
            </p>
          </template>
          <template v-else>
            <p class="text-muted small mb-0">
              Цена токена зависит от тарифа и объёма докупки. Списание по факту использования AI.
            </p>
            <p v-if="wallet.subscription_plan" class="small mb-0 mt-2">
              Тариф: <strong>{{ wallet.subscription_plan.name }}</strong>
              <span v-if="wallet.subscription_renews_at">
                · следующее начисление {{ formatDate(wallet.subscription_renews_at) }}
              </span>
            </p>
          </template>
        </div>
      </div>
    </div>

    <div v-if="showRetailBilling" class="card p-4 mb-4">
      <h2 class="h5 mb-3">Тарифы</h2>
      <p v-if="plansLoading" class="text-muted small mb-0">Загрузка…</p>
      <div v-else class="row g-3">
        <div v-for="plan in plans" :key="plan.id" class="col-md-4">
          <div
            class="border rounded p-3 h-100 d-flex flex-column"
            :class="{ 'border-primary border-2 shadow-sm': isCurrentPlan(plan) }"
          >
            <div v-if="isCurrentPlan(plan)" class="badge bg-primary align-self-start mb-2">Ваш тариф</div>
            <h3 class="h6">{{ plan.name }}</h3>
            <p class="text-muted small flex-grow-1">{{ plan.description || '—' }}</p>
            <p class="mb-1">
              <template v-if="planPriceWas(plan)">
                <span class="text-muted text-decoration-line-through me-2 small">{{ money(planPriceWas(plan)) }}</span>
              </template>
              <strong>{{ money(Number(plan.price_rub)) }}</strong> / мес
            </p>
            <p class="small mb-3">{{ intFmt.format(plan.monthly_tokens) }} токенов / мес</p>
            <button
              v-if="isCurrentPlan(plan)"
              type="button"
              class="btn btn-success btn-sm mt-auto"
              disabled
            >
              Активен
            </button>
            <button
              v-else
              type="button"
              class="btn btn-outline-primary btn-sm mt-auto"
              :disabled="planSelecting === plan.id"
              @click="selectPlan(plan)"
            >
              <span v-if="planSelecting === plan.id" class="spinner-border spinner-border-sm" />
              <template v-else>Выбрать</template>
            </button>
          </div>
        </div>
        <div class="col-md-4">
          <div class="border rounded p-3 h-100 d-flex flex-column">
            <h3 class="h6">Корпоративный</h3>
            <p class="text-muted small flex-grow-1">Индивидуальные лимиты и оплата по договору.</p>
            <p class="mb-3"><strong>По запросу</strong></p>
            <a :href="corporateMailto" class="btn btn-outline-primary btn-sm mt-auto">Запросить условия</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showRetailBilling" class="card p-4 mb-4">
      <h2 class="h5 mb-3">Докупка токенов</h2>
      <p class="text-muted small">Оплата картой через ЮKassa. При отсутствии онлайн-оплаты заказ подтверждается администратором.</p>
      <TokenTopupStepper :ordering="topupOrdering" @order="orderTopupTokens" />
      <div v-if="topupMessage" class="alert small mb-3 mt-3" :class="topupMessageError ? 'alert-danger' : 'alert-info'">{{ topupMessage }}</div>
    </div>

    <div v-if="showRetailBilling" id="cabinet-billing-payments" class="card p-4 mb-4">
      <h2 class="h5 mb-3">История платежей</h2>
      <div v-if="!payments.length" class="text-muted small">Платежей пока нет.</div>
      <div v-else class="table-responsive">
        <table class="table table-sm align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Дата</th>
              <th>Описание</th>
              <th>Тип</th>
              <th>Статус</th>
              <th class="text-end">Сумма</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in payments" :key="`${row.kind}-${row.id}`">
              <td class="text-nowrap small">{{ formatDate(row.paid_at || row.created_at) }}</td>
              <td class="small">{{ row.description || '—' }}</td>
              <td class="small">{{ paymentKindLabel(row.kind) }}</td>
              <td class="small">
                <span class="badge" :class="paymentStatusBadge(row.status)">{{ paymentStatusLabel(row.status) }}</span>
              </td>
              <td class="text-end font-monospace">{{ money(Number(row.amount)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card p-4 mb-4">
      <h2 class="h5 mb-3">История списаний токенов</h2>
      <div v-if="!tokenLedger.length" class="text-muted">Операций пока нет.</div>
      <div v-else class="table-responsive">
        <table class="table table-sm align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Дата</th>
              <th>Операция</th>
              <th>Этап</th>
              <th class="text-end">Токены</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tokenLedger" :key="row.id">
              <td class="text-nowrap small">{{ formatDate(row.created_at) }}</td>
              <td class="small">{{ ledgerOperationLabel(row.operation) }}</td>
              <td class="small">{{ row.stage_label || row.stage || '—' }}</td>
              <td class="text-end font-monospace">{{ formatLedgerDelta(row.tokens_delta ?? row.tokens) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'history'" class="cabinet-section">
            <div class="card p-4 mb-4">
      <h2 class="h5 mb-3">История анализов</h2>

      <div v-if="!history.length" class="text-muted">
        Анализы ещё не выполнялись.
      </div>

      <template v-else>
        <div class="row g-3 align-items-end mb-3">
          <div class="col-md-6">
            <label class="form-label">Отчётный период</label>
            <div class="history-range" ref="historyRangeRef">
              <button type="button" class="history-range__control" @click="toggleHistoryFilter">
                <span>{{ historyRangeLabel }}</span>
                <i class="fa fa-calendar"></i>
              </button>
              <div v-if="historyFilterOpen" class="history-range__popover card shadow-sm">
                <div class="row g-2">
                  <div class="col-12 col-md-6">
                    <label class="form-label small text-muted mb-1">С даты</label>
                    <input v-model="historyFiltersDraft.from" type="date" class="form-control" />
                  </div>
                  <div class="col-12 col-md-6">
                    <label class="form-label small text-muted mb-1">По дату</label>
                    <input v-model="historyFiltersDraft.to" type="date" class="form-control" />
                  </div>
                </div>
                <div class="d-flex flex-column flex-md-row gap-2 mt-3">
                  <button class="btn btn-primary flex-grow-1" type="button" @click="applyHistoryFilters">
                    Применить
                  </button>
                  <button class="btn btn-outline-secondary flex-grow-1" type="button" @click="resetHistoryFilters">
                    Сбросить
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6 d-flex justify-content-md-end justify-content-start gap-2">
            <button class="btn btn-outline-primary ms-auto" type="button" :disabled="!filteredHistory.length"
              @click="exportHistoryToExcel">
              Экспорт Excel
            </button>
          </div>
        </div>

        <div v-if="filteredHistory.length" class="table-responsive">
          <table class="table table-sm align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;"></th>
                <th>Номер тендера</th>
                <th>Наименование закупки</th>
                <th>Дата анализа</th>
                <th class="text-end">Длительность</th>
                <th class="text-end">Стоимость</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in filteredHistory" :key="row.id">
                <tr @click="toggleHistoryRow(row.id)" style="cursor: pointer;">
                  <td class="text-center">
                    <i class="fa" :class="isHistoryRowExpanded(row.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  </td>
                  <td>{{ row.tender_number || row.tender?.tender_number }}</td>
                  <td>{{ row.title || row.tender?.title }}</td>
                  <td>{{ historyDateLabel(row) }}</td>
                  <td class="text-end">
                    {{ formatDuration(row.duration_seconds ?? row.total_duration_seconds) }}
                  </td>
                  <td class="text-end font-monospace">
                    {{ historyRowChargedTokens(row) }}
                  </td>
                </tr>

                <tr v-if="isHistoryRowExpanded(row.id)">
                  <td colspan="6" class="bg-light">
                    <div class="table-responsive mb-0">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Этап</th>
                            <th class="text-end">Длительность</th>
                            <th class="text-end">Стоимость</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="st in row.stages || row.breakdown || []" :key="st.stage">
                            <td>
                              <span v-if="st.stage === 'criteria'">Критерии</span>
                              <span v-else-if="st.stage === 'items'">Товары и услуги</span>
                              <span v-else-if="st.stage === 'prices'">Цены конкурентов</span>
                              <span v-else-if="st.stage === 'winprob'">Вероятность победы</span>
                              <span v-else>{{ st.stage }}</span>
                            </td>
                            <td class="text-end">
                              {{ formatDuration(st.duration_seconds ?? st.duration) }}
                            </td>
                            <td class="text-end font-monospace">
                              {{ historyStageChargedTokens(st) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="text-muted">
          Нет анализов за выбранный период.
        </div>
      </template>
            </div>

            <div class="row g-3 mb-4">
              <div class="col-md-4" v-for="card in statCards" :key="card.label">
                <div class="card text-center p-4">
                  <p class="text-muted">{{ card.label }}</p>
                  <h3>{{ card.value }}</h3>
                </div>
              </div>
            </div>
          </div>
        </template>
      </section>
    </div>

    <!-- <div class="card p-4 mb-4">
      <h2 class="h5">Прайс-листы</h2>

      <form class="row gy-2 gx-2 align-items-end" @submit.prevent="uploadPricelist">
        <div class="col-md-4">
          <input ref="plFile" type="file" accept=".xlsx,.xls" class="form-control" required>
        </div>
        <div class="col-md-4">
          <input v-model="plComment" class="form-control" placeholder="Комментарий (необязательно)">
        </div>
        <div class="col-md-4 d-grid">
          <button class="btn btn-dark">Загрузить</button>
        </div>
      </form>

      <div class="table-responsive mt-3">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>Файл</th>
              <th>Дата</th>
              <th>Позиции</th>
              <th>Комментарий</th>
              <th class="text-center">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in pricelistFiles" :key="f.id">
              <td>
                {{ f.file_name }}

                <a :href="dlUrl(f.id)" target="_blank" rel="noopener" class="ms-2" title="Скачать исходный Excel">
                  <i class="fa-solid fa-download"></i>
                </a>
              </td>

              <td>{{ formatDate(f.uploaded_at) }}</td>
              <td>{{ f.rows }}</td>
              <td>{{ f.comment }}</td>
              <td class="text-center">
                <button class="btn btn-sm btn-link text-danger" title="Удалить файл" @click="deletePricelist(f)">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!pricelistFiles.length">
              <td colspan="5" class="text-center">Нет данных</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div> -->

  </div>
  <TheModal :show="itemsModalShown" @close="itemsModalShown = false">
    <ItemsModal v-if="itemsModalShown" :tender-id="itemsModalTender" />
  </TheModal>
  <TheModal :show="globalItemsModalShown" @close="globalItemsModalShown = false">
    <GlobalItemsModal v-if="globalItemsModalShown" />
  </TheModal>
  <TheModal
    :show="winCriteriaModalShown"
    size="xxl"
    :close-disabled="winCriteriaCloseDisabled"
    @close="onWinCriteriaModalClose"
  >
    <WinCriteriaModal v-if="winCriteriaModalShown" ref="winCriteriaModalRef" />
  </TheModal>
  <ProductGuideModal
    :show="guideShow"
    :guide-id="guideActiveId"
    @confirm="confirmGuide"
    @dismiss="dismissGuide"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, reactive, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as XLSX from 'xlsx'
import api from '../api'
import {
  applySubscriptionPlanCatalog,
  applySubscriptionPlanToWallet,
  CORPORATE_PLAN_DISPLAY,
  referencePriceFromBase,
} from '@/billing/subscriptionPlans'
import EmailInput from '@/components/form/EmailInput.vue'
import PhoneInput from '@/components/form/PhoneInput.vue'
import TokenTopupStepper from '@/components/billing/TokenTopupStepper.vue'
import TheModal from './TheModal.vue'
import ItemsModal from './ItemsModal.vue'
import GlobalItemsModal from './GlobalItemsModal.vue'
import WinCriteriaModal from '../components/WinCriteriaModal.vue'
import { useAuthStore } from '@/store/pinia.js'
import { useUiStore } from '@/store/ui.js'
import { resolveTokenBalance } from '@/utils/tokenBalance'
import { isTrialExpired, trialDaysLeft } from '@/utils/productAccess.js'
import { topupPriceRub } from '@/billing/topupPricing'
import { serializeKanbanStages, sortKanbanStagesByOrder } from '@/utils/kanbanStages'
import { pushEcommerceAdd, trackPurchaseFromPayment } from '@/utils/metrikaEcommerce'
import KanbanStagesEditor from '@/components/lists/KanbanStagesEditor.vue'
import PipelineSettingsList from '@/components/pipeline/PipelineSettingsList.vue'
import {
  normalizePipelineDraft,
  pipelineSummaryLabel,
  readPipelineFlagsFromUser,
  serializePipelineFlags,
} from '@/billing/pipelineStages'
import {
  connectTenderplan,
  disconnectTenderplan,
  getTenderplanIntegration,
  integrationErrorMessage,
} from '@/utils/tenderplanIntegration'
import { syncUserKeys, applyKeySyncToStorage } from '@/utils/keySync'
import { resolveProfileUserId } from '@/utils/resolveProfileUserId'
import ProductGuideModal from '@/components/guide/ProductGuideModal.vue'
import { cabinetGuideId } from '@/constants/productGuides'
import { useProductGuide } from '@/composables/useProductGuide'
import { userShowsRetailBilling } from '@/utils/retailBilling'

const auth = useAuthStore()
const guideUserId = computed(() => resolveProfileUserId(auth.user))
const {
  show: guideShow,
  activeGuideId: guideActiveId,
  openGuide,
  tryAutoShow,
  confirmGuide,
  dismissGuide,
} = useProductGuide(() => guideUserId.value)
const ui = useUiStore()
const route = useRoute()
const router = useRouter()

const cabinetTabs = computed(() => [
  { key: 'profile', label: 'Профиль' },
  { key: 'settings', label: 'Настройки' },
  { key: 'integration', label: 'Интеграция' },
  { key: 'catalog', label: 'Справочники' },
  { key: 'archives', label: 'Списки' },
  // Баланс и ledger всегда видны; тарифы/докупка/ЮKassa — только при showRetailBilling
  { key: 'billing', label: 'Биллинг' },
  { key: 'history', label: 'История' },
])

const activeTab = ref('profile')

function selectCabinetTab(tab) {
  if (!tab?.key) return
  activeTab.value = tab.key
}

function openCabinetGuide() {
  const id = cabinetGuideId(activeTab.value)
  if (id) openGuide(id, { auto: false })
}

const billingReturnBanner = ref(null)
const topupMessageError = ref(false)

const tpIntegration = ref({
  connected: false,
  token_hint: null,
  keys_count: 0,
  last_sync_at: null,
})
const tpTokenInput = ref('')
const tpIntegrationLoading = ref(false)
const tpIntegrationMessage = ref('')
const tpIntegrationError = ref(false)

const API_BASE = (api.defaults?.baseURL || '/api').replace(/\/+$/, '')



/* --- helpers --------------------------------------------------------- */
const money = v => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(v)

const parseDateValue = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const num = Number(value)
    if (!Number.isFinite(num)) return null
    const ms = num > 1e12 ? num : num > 1e9 ? num * 1000 : null
    if (ms == null) return null
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null

    // ISO / RFC formats
    const iso = new Date(trimmed)
    if (!Number.isNaN(iso.getTime())) return iso

    // DD.MM.YYYY [HH:MM:SS]
    const ddmmyyyy = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/)
    if (ddmmyyyy) {
      const [, dd, mm, yyyy, hh = '00', min = '00', ss = '00'] = ddmmyyyy
      const asIso = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`
      const parsed = new Date(asIso)
      if (!Number.isNaN(parsed.getTime())) return parsed
    }
  }
  return null
}

const pickLatestDate = (values = []) => {
  const normalized = values
    .map(value => parseDateValue(value))
    .filter(Boolean)
  if (!normalized.length) return null
  return new Date(Math.max(...normalized.map(date => date.getTime())))
}

const formatDate = d => {
  const value = parseDateValue(d)
  return value ? value.toLocaleDateString('ru-RU') : '—'
}

const formatDisplayDate = (value) => {
  const date = parseDateValue(value)
  return date ? date.toLocaleDateString('ru-RU') : ''
}

const formatDuration = secs => {
  const s = Number(secs || 0)
  if (!s) return '—'
  const minutes = Math.floor(s / 60)
  const seconds = Math.round(s % 60)
  if (minutes <= 0) return `${seconds} с`
  if (minutes < 60) return `${minutes} мин ${seconds.toString().padStart(2, '0')} с`
  const hours = Math.floor(minutes / 60)
  const m2 = minutes % 60
  return `${hours} ч ${m2.toString().padStart(2, '0')} мин`
}

const dlUrl = id => `${API_BASE}/pricelist-files/${id}/download/`

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const defaultHistoryRange = () => {
  const now = new Date()
  const from = new Date(now)
  from.setMonth(from.getMonth() - 1)
  return {
    from: formatDateInput(from),
    to: formatDateInput(now)
  }
}

const parseDateInput = (value) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const resolveHistoryDate = (row) => {
  if (!row) return null
  const candidates = [
    row.finished_at,
    row.finishedAt,
    row.completed_at,
    row.completedAt,
    row.analyzed_at,
    row.analyzedAt,
    row.analysis_finished_at,
    row.analysisFinishedAt,
    row.analysis_date,
    row.analysisDate,
    row.updated_at,
    row.updatedAt,
    row.created_at,
    row.createdAt,
    row.created,
    row.updated,
    row?.tender?.finished_at,
    row?.tender?.updated_at,
    row?.tender?.created_at,
    row?.tender?.created
  ]
  const primary = pickLatestDate(candidates)
  if (primary) return primary

  const timingValues = []
  const timing = row.timings
  if (timing && typeof timing === 'object') {
    Object.values(timing).forEach((entry) => {
      if (!entry || typeof entry !== 'object') return
      timingValues.push(entry.finished_at, entry.finishedAt, entry.ts)
    })
  }
  const timingDate = pickLatestDate(timingValues)
  if (timingDate) return timingDate

  const stageLists = [
    Array.isArray(row.stages) ? row.stages : [],
    Array.isArray(row.breakdown) ? row.breakdown : [],
    Array.isArray(row.billing?.stages) ? row.billing.stages : []
  ]
  const stageDates = stageLists
    .flat()
    .map(stage => stage && typeof stage === 'object'
      ? pickLatestDate([stage.ts, stage.finished_at, stage.finishedAt])
      : null)
    .filter(Boolean)
  const stageDate = pickLatestDate(stageDates)
  if (stageDate) return stageDate

  return null
}

/* --- состояние ------------------------------------------------------- */
const loading = ref(true)

watch(
  [activeTab, loading, guideUserId],
  ([tab, isLoading, uid]) => {
    if (isLoading || !uid) return
    const id = cabinetGuideId(tab)
    if (id) tryAutoShow(id)
  },
)

const archives = ref([])

/* профиль */
const profile = ref({
  name: '',
  email: '',
  organization_name: '',
  phone: '',
  token_balance: 0,
  kanban_default_stages: [],
})
/** null в token_balance = ещё не пришёл ответ /billing/wallet/, не подменяем баланс из профиля нулём */
const wallet = ref({
  token_balance: null,
  subscription_plan: null,
  subscription_renews_at: null,
  trial_ends_at: null,
  can_use_product: null,
  trial_expired: null,
  has_subscription: null,
  billing_b2b_wire: false,
  billing_b2b_monthly_tokens: null,
  billing_show_retail: false,
})

const isB2bWireBilling = computed(() =>
  Boolean(
    wallet.value?.billing_b2b_wire
      ?? profile.value?.billing_b2b_wire
      ?? auth.user?.billing_b2b_wire,
  ),
)

const showRetailBilling = computed(() =>
  userShowsRetailBilling({
    billing_b2b_wire:
      wallet.value?.billing_b2b_wire
      ?? profile.value?.billing_b2b_wire
      ?? auth.user?.billing_b2b_wire,
    billing_show_retail:
      wallet.value?.billing_show_retail
      ?? profile.value?.billing_show_retail
      ?? auth.user?.billing_show_retail,
  }),
)

const b2bMonthlyTokensDisplay = computed(() => {
  const raw =
    wallet.value?.billing_b2b_monthly_tokens
    ?? profile.value?.billing_b2b_monthly_tokens
    ?? auth.user?.billing_b2b_monthly_tokens
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
})

const trialStatusBanner = computed(() => {
  const w = wallet.value
  if (w.has_subscription || auth.user?.has_subscription) return null
  const ends = w.trial_ends_at || auth.user?.trial_ends_at
  if (!ends && !auth.user?.email_verified) return null
  if (isTrialExpired(w) || isTrialExpired(auth.user)) {
    return {
      variant: 'alert-warning',
      text: isB2bWireBilling.value
        ? 'Пробный период завершён. Обратитесь к администратору для продления доступа.'
        : 'Пробный период завершён. Выберите тариф ниже, чтобы снова открыть поиск и анализ закупок.',
    }
  }
  const days = trialDaysLeft(w) ?? trialDaysLeft(auth.user)
  if (days == null) return null
  return {
    variant: 'alert-info',
    text: `Пробный доступ активен ещё ${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'} (до ${formatDate(ends)}).`,
  }
})
const plans = ref([])
const plansLoading = ref(true)
const planSelecting = ref(null)
const topupOrdering = ref(false)
const corporateMailto = `mailto:${CORPORATE_PLAN_DISPLAY.contactEmail}?subject=${encodeURIComponent('Корпоративный тариф AI-Tenders')}`
const topupMessage = ref('')
const tokenLedger = ref([])
const intFmt = new Intl.NumberFormat('ru-RU')

function formatChargedTokens(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return '—'
  return intFmt.format(n)
}

function usageEntryChargedTokens(entry) {
  if (!entry || typeof entry !== 'object') return 0
  const raw = entry.tokens_charged ?? entry.cost_rub ?? null
  if (raw == null || raw === '') return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function historyRowChargedTokens(row) {
  const direct = row?.total_cost_rub ?? row?.total_cost ?? null
  if (direct != null && direct !== '') {
    const formatted = formatChargedTokens(direct)
    if (formatted !== '—') return formatted
  }
  const stages = row?.stages || row?.breakdown || []
  const sum = stages.reduce((acc, st) => acc + usageEntryChargedTokens(st), 0)
  return sum > 0 ? formatChargedTokens(sum) : '—'
}

function historyStageChargedTokens(stage) {
  return formatChargedTokens(usageEntryChargedTokens(stage) || null)
}

const kanbanTemplateStages = ref([])

function syncKanbanTemplateStagesFromProfile() {
  const raw = profile.value?.kanban_default_stages
  kanbanTemplateStages.value = sortKanbanStagesByOrder(
    Array.isArray(raw) ? raw.map((s) => ({ ...s })) : [],
  )
}

const kanbanSaving = ref(false)
const kanbanSaveMessage = ref('')
const kanbanSaveError = ref(false)

const pipelineDraft = reactive(readPipelineFlagsFromUser(null))
const pipelineSaving = ref(false)
const pipelineSaveMessage = ref('')
const pipelineSaveError = ref(false)

const pipelineEnabledLabel = computed(() => pipelineSummaryLabel(pipelineDraft))

const notifications = ref({
  email_alerts: true,
  email_billing: true,
  email_low_balance: true,
  email_roadmap_overdue: true,
})
const notificationsSaving = ref(false)
const notificationsSaveMessage = ref('')
const notificationsSaveError = ref(false)
const passwordChangeSending = ref(false)
const passwordChangeMessage = ref('')
const passwordChangeError = ref(false)

async function loadNotifications() {
  try {
    const { data } = await api.get('/notifications/')
    notifications.value = {
      email_alerts: data?.email_alerts !== false,
      email_billing: data?.email_billing !== false,
      email_low_balance: data?.email_low_balance !== false,
      email_roadmap_overdue: data?.email_roadmap_overdue !== false,
    }
  } catch (e) {
    console.warn('[TheCabinet] notifications load failed', e)
  }
}

async function saveNotifications() {
  notificationsSaving.value = true
  notificationsSaveMessage.value = ''
  notificationsSaveError.value = false
  try {
    const { data } = await api.patch('/notifications/', notifications.value)
    notifications.value = {
      email_alerts: data?.email_alerts !== false,
      email_billing: data?.email_billing !== false,
      email_low_balance: data?.email_low_balance !== false,
      email_roadmap_overdue: data?.email_roadmap_overdue !== false,
    }
    notificationsSaveMessage.value = 'Настройки уведомлений сохранены.'
  } catch (e) {
    notificationsSaveError.value = true
    notificationsSaveMessage.value =
      e?.response?.data?.detail || 'Не удалось сохранить настройки.'
  } finally {
    notificationsSaving.value = false
  }
}

async function requestPasswordChange() {
  passwordChangeSending.value = true
  passwordChangeError.value = false
  passwordChangeMessage.value = ''
  try {
    const { data } = await api.post('/auth/change-password/request/')
    passwordChangeMessage.value =
      data?.detail || 'Письмо со ссылкой для смены пароля отправлено.'
  } catch (e) {
    passwordChangeError.value = true
    passwordChangeMessage.value =
      e?.response?.data?.detail || 'Не удалось отправить письмо. Попробуйте позже.'
  } finally {
    passwordChangeSending.value = false
  }
}

const tokenBalanceFormatted = computed(() => {
  const n = resolveTokenBalance({
    token_balance:
      wallet.value?.token_balance ??
      profile.value?.token_balance ??
      auth.user?.token_balance,
  })
  return intFmt.format(n)
})

function ledgerOperationLabel(op) {
  const map = {
    debit_ai: 'Списание AI',
    credit_subscription: 'Подписка',
    credit_topup: 'Докупка',
    credit_admin: 'Начисление',
    refund_admin: 'Возврат',
  }
  return map[op] || op || '—'
}

function formatLedgerDelta(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return sign + intFmt.format(n)
}

function paymentKindLabel(kind) {
  const map = {
    topup: 'Докупка токенов',
    subscription: 'Подписка',
    legacy: 'Платёж (архив)',
  }
  return map[kind] || kind || '—'
}

function paymentStatusLabel(status) {
  const map = {
    pending: 'Ожидает оплаты',
    succeeded: 'Оплачен',
    canceled: 'Отменён',
    cancelled: 'Отменён',
  }
  return map[status] || status || '—'
}

function paymentStatusBadge(status) {
  if (status === 'succeeded') return 'bg-success'
  if (status === 'pending') return 'bg-warning text-dark'
  if (status === 'canceled' || status === 'cancelled') return 'bg-secondary'
  return 'bg-light text-dark'
}

const YOOKASSA_CHECKOUT_KEY = 'yookassa_checkout_context'

function saveYookassaCheckoutContext(data) {
  if (!data) return
  const ctx = {
    billing_payment_id: data.billing_payment_id ?? null,
    payment_id: data.payment_id ?? null,
    order_id: data.id ?? null,
    kind: data.kind || (data.package != null ? 'topup' : 'subscription'),
    product_id: data.product_id ?? null,
    product_name: data.product_name ?? null,
    product_price: data.product_price ?? null,
    product_category: data.product_category ?? null,
    tokens: data.tokens ?? null,
    ts: Date.now(),
  }
  try {
    sessionStorage.setItem(YOOKASSA_CHECKOUT_KEY, JSON.stringify(ctx))
  } catch (_) {}
}

function buildSubscriptionCheckoutContext(plan, apiData) {
  return {
    ...apiData,
    kind: 'subscription',
    product_id: plan.slug || String(plan.id),
    product_name: plan.name,
    product_price: plan.price_rub,
    product_category: 'Subscription',
  }
}

function buildTopupCheckoutContext(tokens, apiData) {
  const price = apiData.amount ?? apiData.price_rub ?? topupPriceRub(tokens)
  return {
    ...apiData,
    kind: 'topup',
    tokens,
    product_id: `topup_${tokens}`,
    product_name: `Докупка ${tokens} токенов`,
    product_price: price,
    product_category: 'TopUp',
  }
}

function trackCheckoutAdd(checkoutContext) {
  if (!checkoutContext?.product_id && !checkoutContext?.product_name) return
  pushEcommerceAdd({
    id: checkoutContext.product_id,
    name: checkoutContext.product_name,
    price: checkoutContext.product_price,
    category: checkoutContext.product_category,
  })
}

function loadYookassaCheckoutContext() {
  try {
    const raw = sessionStorage.getItem(YOOKASSA_CHECKOUT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

function clearYookassaCheckoutContext() {
  try {
    sessionStorage.removeItem(YOOKASSA_CHECKOUT_KEY)
  } catch (_) {}
}

function findPaymentForCheckoutReturn(list, ctx) {
  if (!ctx || !Array.isArray(list) || !list.length) return null
  if (ctx.billing_payment_id != null) {
    const apiId = Number(ctx.billing_payment_id) + 1_000_000
    const hit = list.find((p) => Number(p.id) === apiId)
    if (hit) return hit
  }
  if (ctx.order_id != null) {
    const needle = `заказ #${ctx.order_id}`
    const hit = list.find((p) => String(p.description || '').includes(needle))
    if (hit) return hit
  }
  return null
}

function billingReturnMessage(payment) {
  if (!payment) {
    return {
      variant: 'alert-warning',
      text: 'Возврат с оплаты. Не удалось определить этот платёж — проверьте строку в истории ниже.',
    }
  }
  const st = payment.status
  if (st === 'succeeded') {
    return {
      variant: 'alert-success',
      text: 'Оплата подтверждена. Баланс и история платежей обновлены.',
    }
  }
  if (st === 'canceled' || st === 'cancelled') {
    return {
      variant: 'alert-warning',
      text: 'Оплата отменена или не завершена. Средства не списаны, токены не начислены.',
    }
  }
  if (st === 'pending') {
    return {
      variant: 'alert-info',
      text: 'Платёж ещё в обработке. Если вы отменили оплату, статус скоро сменится на «Отменён» — обновите страницу.',
    }
  }
  return {
    variant: 'alert-info',
    text: `Статус платежа: ${paymentStatusLabel(st)}.`,
  }
}

async function syncYookassaPayments({ billingPaymentId, abandonIfUnpaid = false } = {}) {
  const body = { abandon_if_unpaid: abandonIfUnpaid }
  if (billingPaymentId != null) body.billing_payment_id = billingPaymentId
  try {
    await api.post('/billing/payments/sync/', body)
  } catch (e) {
    console.warn('[TheCabinet] YooKassa payment sync failed', e)
  }
}

async function fetchPaymentsList() {
  const { data: pays } = await api.get('/billing/payments/')
  payments.value = Array.isArray(pays) ? pays : []
  return payments.value
}

async function pollCheckoutPaymentStatus(ctx) {
  if (ctx?.billing_payment_id != null) {
    await syncYookassaPayments({
      billingPaymentId: ctx.billing_payment_id,
      abandonIfUnpaid: true,
    })
  }
  const attempts = ctx ? 4 : 1
  let last = null
  for (let i = 0; i < attempts; i += 1) {
    const list = await fetchPaymentsList()
    last = findPaymentForCheckoutReturn(list, ctx) || last
    if (last && last.status !== 'pending') break
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000))
    }
  }
  return last
}

function redirectToPayment(url, checkoutContext) {
  const target = (url || '').trim()
  if (!target) return false
  trackCheckoutAdd(checkoutContext)
  saveYookassaCheckoutContext(checkoutContext)
  window.location.href = target
  return true
}

async function handleBillingReturnQuery() {
  if (!showRetailBilling.value || route.query?.billing !== 'return') return
  if (isB2bWireBilling.value) {
    activeTab.value = 'billing'
    const nextQuery = { ...route.query }
    delete nextQuery.billing
    router.replace({ path: route.path, query: nextQuery }).catch(() => {})
    return
  }
  const ctx = loadYookassaCheckoutContext()
  billingReturnBanner.value = {
    variant: 'alert-info',
    text: 'Проверяем статус последней оплаты…',
  }
  try {
    const payment = await pollCheckoutPaymentStatus(ctx)
    if (payment?.status === 'succeeded') {
      trackPurchaseFromPayment(ctx, payment)
    }
    await loadBilling()
    billingReturnBanner.value = billingReturnMessage(payment)
  } catch (e) {
    console.warn('[TheCabinet] billing return refresh failed', e)
    billingReturnBanner.value = {
      variant: 'alert-warning',
      text: 'Не удалось обновить статус оплаты. Откройте историю платежей ниже.',
    }
  } finally {
    clearYookassaCheckoutContext()
  }
  activeTab.value = 'billing'
  await nextTick()
  document.getElementById('cabinet-billing-payments')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const nextQuery = { ...route.query }
  delete nextQuery.billing
  router.replace({ path: route.path, query: nextQuery }).catch(() => {})
}

async function loadBilling() {
  plansLoading.value = true
  try {
    const { data: wData } = await api.get('/billing/wallet/')
    wallet.value = applySubscriptionPlanToWallet(wData || {})
    profile.value.billing_b2b_wire = Boolean(wallet.value.billing_b2b_wire)
    profile.value.billing_b2b_monthly_tokens = wallet.value.billing_b2b_monthly_tokens ?? null

    const ledgerReq = api.get('/billing/ledger/')
    const plansReq = wallet.value.billing_b2b_wire
      ? Promise.resolve({ data: [] })
      : api.get('/billing/plans/')
    const [pl, ledger] = await Promise.all([plansReq, ledgerReq])

    plans.value = wallet.value.billing_b2b_wire
      ? []
      : applySubscriptionPlanCatalog(Array.isArray(pl.data) ? pl.data : [])
    tokenLedger.value = Array.isArray(ledger.data) ? ledger.data : []
    if (wallet.value.token_balance != null) {
      profile.value.token_balance = wallet.value.token_balance
    }
    const authPatch = {}
    if (wallet.value.token_balance != null) authPatch.token_balance = wallet.value.token_balance
    if (Object.prototype.hasOwnProperty.call(wallet.value, 'subscription_renews_at')) {
      authPatch.subscription_renews_at = wallet.value.subscription_renews_at
    }
    if (wallet.value.trial_ends_at != null) authPatch.trial_ends_at = wallet.value.trial_ends_at
    if (wallet.value.can_use_product != null) authPatch.can_use_product = wallet.value.can_use_product
    if (wallet.value.trial_expired != null) authPatch.trial_expired = wallet.value.trial_expired
    if (wallet.value.has_subscription != null) authPatch.has_subscription = wallet.value.has_subscription
    if (Object.keys(authPatch).length) auth.patchUser(authPatch)
  } catch (e) {
    console.warn('[TheCabinet] billing load failed', e)
  } finally {
    plansLoading.value = false
  }
}

async function selectPlan(plan) {
  if (!plan?.slug && !plan?.id) return
  planSelecting.value = plan.id
  try {
    const { data } = await api.post('/billing/subscription/select/', {
      plan_slug: plan.slug,
      slug: plan.slug,
      plan_id: plan.id,
    })
    const payUrl = data.payment_url || data.confirmation_url
    if (data.payment_required && payUrl) {
      redirectToPayment(payUrl, buildSubscriptionCheckoutContext(plan, data))
      return
    }
    wallet.value = applySubscriptionPlanToWallet({ ...wallet.value, ...data })
    if (data.token_balance != null) profile.value.token_balance = data.token_balance
    const authPatch = {}
    if (data.token_balance != null) authPatch.token_balance = data.token_balance
    if (Object.prototype.hasOwnProperty.call(data, 'subscription_renews_at')) {
      authPatch.subscription_renews_at = data.subscription_renews_at
    }
    authPatch.can_use_product = true
    authPatch.has_subscription = true
    authPatch.trial_expired = false
    if (Object.keys(authPatch).length) auth.patchUser(authPatch)
    alert(`Тариф «${plan.name}» активирован.`)
    await loadBilling()
  } catch (e) {
    alert(e?.response?.data?.detail || 'Не удалось выбрать тариф.')
  } finally {
    planSelecting.value = null
  }
}

function planPriceWas(plan) {
  if (!plan?.monthly_tokens || plan.slug === 'starter') return null
  const ref = referencePriceFromBase(plan.monthly_tokens)
  const price = Number(plan.price_rub)
  return ref > price ? ref : null
}

async function orderTopupTokens(tokens) {
  topupOrdering.value = true
  topupMessage.value = ''
  topupMessageError.value = false
  try {
    const { data } = await api.post('/billing/topup/orders/', { tokens })
    const payUrl = data.payment_url || data.confirmation_url
    if (data.payment_required !== false && payUrl) {
      redirectToPayment(payUrl, buildTopupCheckoutContext(tokens, data))
      return
    }
    topupMessage.value = data?.detail || 'Заказ создан. Ожидайте подтверждения оплаты.'
  } catch (e) {
    topupMessageError.value = true
    topupMessage.value = e?.response?.data?.detail || 'Не удалось создать заказ.'
  } finally {
    topupOrdering.value = false
  }
}

/* статистика */
const stats = ref({ analyzed: 0, participated: 0, won: 0 })

const statCards = computed(() => [
  { label: 'Проанализировано тендеров', value: stats.value.analyzed || 0 },
  {
    label: 'Участие в тендерах',
    value: (stats.value.participated ?? stats.value.total ?? 0)
  },
  { label: 'Выиграно', value: stats.value.won || 0 }
])

/* история тендеров */
const history = ref([])
const historyFilters = ref(defaultHistoryRange())
const historyFiltersDraft = ref({ ...historyFilters.value })
const historyFilterOpen = ref(false)
const historyRangeRef = ref(null)

const filteredHistory = computed(() => {
  const from = parseDateInput(historyFilters.value.from)
  const to = parseDateInput(historyFilters.value.to)
  const startBoundary = from ? new Date(from.setHours(0, 0, 0, 0)) : null
  const endBoundary = to ? new Date(to.setHours(23, 59, 59, 999)) : null
  return history.value.filter((row) => {
    const date = resolveHistoryDate(row)
    if (!date) return true
    if (startBoundary && date < startBoundary) return false
    if (endBoundary && date > endBoundary) return false
    return true
  })
})

const historyDateLabel = (row) => {
  const date = resolveHistoryDate(row)
  return date ? formatDate(date) : '—'
}

const historyRangeLabel = computed(() => {
  const from = formatDisplayDate(historyFilters.value.from)
  const to = formatDisplayDate(historyFilters.value.to)
  if (from && to) return `${from} — ${to}`
  if (from) return `с ${from}`
  if (to) return `по ${to}`
  return 'Выберите период'
})

const syncHistoryDraft = () => {
  historyFiltersDraft.value = { ...historyFilters.value }
}

const normalizeRange = (range) => {
  const from = parseDateInput(range.from)
  const to = parseDateInput(range.to)
  if (from && to && from > to) {
    return {
      from: formatDateInput(to),
      to: formatDateInput(from)
    }
  }
  return {
    from: from ? formatDateInput(from) : '',
    to: to ? formatDateInput(to) : ''
  }
}

const resetHistoryFilters = () => {
  historyFilters.value = defaultHistoryRange()
  syncHistoryDraft()
  historyFilterOpen.value = false
}

const exportHistoryToExcel = () => {
  if (!filteredHistory.value.length) return
  const dataset = filteredHistory.value.map((row, idx) => ({
    '№': idx + 1,
    'Номер тендера': row.tender_number || row.tender?.tender_number || '—',
    'Название': row.title || row.tender?.title || '—',
    'Дата анализа': historyDateLabel(row),
    'Длительность': formatDuration(row.duration_seconds ?? row.total_duration_seconds),
    'Стоимость (токены)': historyRowChargedTokens(row)
  }))
  const worksheet = XLSX.utils.json_to_sheet(dataset)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'История')
  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
  const filename = `tender-history-${stamp}.xlsx`
  XLSX.writeFile(workbook, filename)
}

const toggleHistoryFilter = () => {
  if (!historyFilterOpen.value) {
    syncHistoryDraft()
  }
  historyFilterOpen.value = !historyFilterOpen.value
}

const applyHistoryFilters = () => {
  historyFilters.value = normalizeRange(historyFiltersDraft.value)
  historyFilterOpen.value = false
}

watch(historyFilters, syncHistoryDraft, { deep: true })

const handleHistoryFilterClickOutside = (event) => {
  if (!historyFilterOpen.value) return
  const root = historyRangeRef.value
  if (root && !root.contains(event.target)) {
    historyFilterOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleHistoryFilterClickOutside))
onUnmounted(() => document.removeEventListener('click', handleHistoryFilterClickOutside))

const expandedHistoryIds = ref([])

const toggleHistoryRow = id => {
  const arr = expandedHistoryIds.value
  const idx = arr.indexOf(id)
  if (idx === -1) arr.push(id)
  else arr.splice(idx, 1)
}

const isHistoryRowExpanded = id => expandedHistoryIds.value.includes(id)

/* Прайс-листы */
const subscription = ref(null)
const payments = ref([])

/** Текущий тариф: кошелёк или ответ GET /billing/subscription/ (тот же план в БД). */
function isCurrentPlan(plan) {
  if (!plan?.id) return false
  const fromWallet = wallet.value?.subscription_plan
  const fromSub = subscription.value
  const cur = fromWallet || fromSub
  if (!cur) return false
  if (cur.id != null && plan.id != null && Number(cur.id) === Number(plan.id)) return true
  if (cur.slug && plan.slug && String(cur.slug) === String(plan.slug)) return true
  return false
}
const pricelistFiles = ref([])
const plComment = ref('')
const plFile = ref(null)


const winCriteriaModalShown = ref(false)
const winCriteriaModalRef = ref(null)
const winCriteriaCloseDisabled = computed(() => winCriteriaModalRef.value?.isQuizBusy?.() ?? false)

function onWinCriteriaModalClose() {
  if (winCriteriaModalRef.value?.isQuizBusy?.()) return
  winCriteriaModalShown.value = false
}

watch(
  () => ui.pendingOpenCabinetWinCriteria,
  (v) => {
    if (v) {
      activeTab.value = 'catalog'
      winCriteriaModalShown.value = true
      ui.consumeOpenCabinetWinCriteria()
    }
  },
)

const itemsModalShown = ref(false)
const itemsModalTender = ref(null)
function openItemsModal(row) {
  itemsModalTender.value =
    row.tender?.id ?? row.tenderId ?? row.id
  itemsModalShown.value = true
}

const globalItemsModalShown = ref(false)



// async function loadPricelists() {
//   const { data } = await api.get('/pricelist-files/')
//   pricelistFiles.value = data
// }

// async function uploadPricelist() {
//   const fileInput = plFile.value
//   if (!fileInput?.files?.length) return
//   const fd = new FormData()
//   fd.append('file', fileInput.files[0])
//   fd.append('comment', plComment.value)
//   await api.post('/pricelist-files/upload/', fd)
//   plComment.value = ''
//   fileInput.value = ''       // reset
//   await loadPricelists()
//   alert('Файл обработан')
// }

// async function deletePricelist(pl) {
//   if (!confirm(`Удалить файл «${pl.file_name}»?`)) return
//   await api.delete(`/pricelist-files/${pl.id}/delete/`)
//   await loadPricelists()
// }




/* --- API calls ------------------------------------------------------- */
async function loadAll() {
  const [
    { data: prof },
    { data: stat },
    { data: hist },
    { data: archs },
  ] = await Promise.all([
    api.get('/profile/'),
    api.get('/analytics/'),
    api.get('/tenders/history/'),
    api.get('/archives/'),
  ])
  profile.value = {
    ...prof,
    kanban_default_stages: sortKanbanStagesByOrder(Array.isArray(prof.kanban_default_stages) ? prof.kanban_default_stages : []),
  }
  syncKanbanTemplateStagesFromProfile()
  Object.assign(pipelineDraft, readPipelineFlagsFromUser(prof))
  auth.patchUser(prof)
  stats.value = stat
  history.value = hist
  archives.value = archs

  if (!prof.billing_b2b_wire) {
    const [{ data: sub }, { data: pays }] = await Promise.all([
      api.get('/billing/subscription/'),
      api.get('/billing/payments/'),
    ])
    subscription.value = sub
    payments.value = pays
  } else {
    subscription.value = null
    payments.value = []
  }

  loading.value = false
  await Promise.all([loadBilling(), loadNotifications(), loadTenderplanIntegration()])
}

async function loadTenderplanIntegration() {
  try {
    const data = await getTenderplanIntegration(api)
    tpIntegration.value = {
      connected: !!data.connected,
      token_hint: data.token_hint ?? null,
      keys_count: data.keys_count ?? 0,
      last_sync_at: data.last_sync_at ?? null,
    }
  } catch (_) {
    tpIntegration.value = { connected: false, token_hint: null, keys_count: 0, last_sync_at: null }
  }
}

function emitTenderplanKeysChanged() {
  window.dispatchEvent(new CustomEvent('tenderplan-integration-changed'))
}

async function saveTenderplanIntegration() {
  tpIntegrationMessage.value = ''
  tpIntegrationError.value = false
  const token = tpTokenInput.value.trim()
  if (!token) return
  tpIntegrationLoading.value = true
  try {
    const data = await connectTenderplan(api, token)
    tpIntegration.value = {
      connected: !!data.connected,
      token_hint: data.token_hint ?? null,
      keys_count: data.keys_count ?? data.bindings_count ?? 0,
      last_sync_at: data.last_sync_at ?? null,
    }
    tpTokenInput.value = ''
    tpIntegrationMessage.value = 'Интеграция сохранена, поисковые ключи загружены.'
    const userId = resolveProfileUserId(profile.value)
    if (userId != null) {
      try {
        const syncResult = await syncUserKeys(api)
        applyKeySyncToStorage(userId, syncResult)
      } catch (_) {}
    }
    emitTenderplanKeysChanged()
  } catch (e) {
    tpIntegrationError.value = true
    tpIntegrationMessage.value = integrationErrorMessage(e)
  } finally {
    tpIntegrationLoading.value = false
  }
}

async function syncTenderplanKeys() {
  tpIntegrationMessage.value = ''
  tpIntegrationError.value = false
  tpIntegrationLoading.value = true
  try {
    const userId = resolveProfileUserId(profile.value)
    const syncResult = await syncUserKeys(api)
    if (userId != null) applyKeySyncToStorage(userId, syncResult)
    await loadTenderplanIntegration()
    tpIntegrationMessage.value = 'Синхронизация выполнена.'
    emitTenderplanKeysChanged()
  } catch (e) {
    tpIntegrationError.value = true
    tpIntegrationMessage.value = e?.response?.data?.detail || 'Не удалось синхронизировать ключи.'
  } finally {
    tpIntegrationLoading.value = false
  }
}

async function removeTenderplanIntegration() {
  if (!window.confirm('Отключить Tenderplan? Все привязанные поисковые ключи будут удалены из аккаунта.')) {
    return
  }
  tpIntegrationMessage.value = ''
  tpIntegrationError.value = false
  tpIntegrationLoading.value = true
  try {
    await disconnectTenderplan(api)
    tpIntegration.value = { connected: false, token_hint: null, keys_count: 0, last_sync_at: null }
    tpTokenInput.value = ''
    const userId = resolveProfileUserId(profile.value)
    if (userId != null) {
      try {
        localStorage.removeItem(`selected_key_${userId}`)
      } catch (_) {}
    }
    tpIntegrationMessage.value = 'Интеграция отключена.'
    emitTenderplanKeysChanged()
  } catch (e) {
    tpIntegrationError.value = true
    tpIntegrationMessage.value = integrationErrorMessage(e)
  } finally {
    tpIntegrationLoading.value = false
  }
}

async function saveProfile() {
  const { name, email, organization_name, phone, kanban_default_stages } = profile.value
  await api.put('/profile/', {
    name,
    email,
    organization_name,
    phone,
    kanban_default_stages: Array.isArray(kanban_default_stages) ? kanban_default_stages : [],
  })
  alert('Профиль сохранён')
}

async function savePipelineSettings() {
  pipelineSaveMessage.value = ''
  pipelineSaveError.value = false
  normalizePipelineDraft(pipelineDraft)
  pipelineSaving.value = true
  try {
    const { name, email, organization_name, phone, kanban_default_stages } = profile.value
    const payload = {
      name,
      email,
      organization_name,
      phone,
      kanban_default_stages: Array.isArray(kanban_default_stages) ? kanban_default_stages : [],
      ...serializePipelineFlags(pipelineDraft),
    }
    const { data } = await api.put('/profile/', payload)
    Object.assign(pipelineDraft, readPipelineFlagsFromUser(data))
    auth.patchUser(data)
    pipelineSaveMessage.value = 'Настройки AI сохранены'
  } catch (e) {
    pipelineSaveError.value = true
    pipelineSaveMessage.value =
      e?.response?.data?.pipeline_auto_roadmap_from_compliance?.[0]
      || e?.response?.data?.detail
      || 'Не удалось сохранить настройки'
  } finally {
    pipelineSaving.value = false
  }
}

async function saveKanbanDefaults() {
  kanbanSaveMessage.value = ''
  kanbanSaveError.value = false
  const stages = serializeKanbanStages(kanbanTemplateStages.value || [])
  kanbanSaving.value = true
  try {
    const { name, email, organization_name, phone } = profile.value
    await api.put('/profile/', {
      name,
      email,
      organization_name,
      phone,
      kanban_default_stages: stages,
    })
    profile.value.kanban_default_stages = stages.map((s) => ({ ...s }))
    kanbanTemplateStages.value = stages.map((s) => ({ ...s }))
    kanbanSaveMessage.value = 'Шаблон сохранён'
  } catch (e) {
    kanbanSaveError.value = true
    kanbanSaveMessage.value = e?.response?.data?.detail || 'Не удалось сохранить'
  } finally {
    kanbanSaving.value = false
  }
}
async function initCabinet() {
  const billingReturn = route.query?.billing === 'return'
  await loadAll()
  if (billingReturn) await handleBillingReturnQuery()
}

onMounted(initCabinet)
</script>

<style scoped>
.cabinet-layout {
  display: flex;
  gap: 1.5rem;
  min-height: 420px;
}

.cabinet-sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 220px;
  flex-shrink: 0;
}

.cabinet-tab {
  border: 1px solid transparent;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  text-align: left;
  background: #f8f9fa;
  color: #212529;
  font-weight: 600;
  box-shadow: 0 18px 36px -24px rgba(15, 23, 42, 0.35);
  transition: background-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.cabinet-tab:hover {
  background: #eef4ff;
  transform: translateX(4px);
}

.cabinet-tab.active {
  background: #0d6efd;
  color: #fff;
  box-shadow: 0 24px 48px -28px rgba(13, 110, 253, 0.55);
}

.cabinet-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

.cabinet-section {
  animation: cabinet-section-in 0.25s ease both;
}

@keyframes cabinet-section-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .cabinet-layout {
    flex-direction: column;
  }

  .cabinet-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
    width: auto;
  }

  .cabinet-tab {
    flex: 1 1 calc(50% - 0.5rem);
  }

  .cabinet-main {
    max-height: none;
    padding-right: 0;
  }
}

@media (max-width: 575.98px) {
  .cabinet-tab {
    flex: 1 1 100%;
    text-align: left;
  }
}

.history-range {
  position: relative;
}

.history-range__control {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.65rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
}

.history-range__control:hover {
  border-color: #0d6efd;
}

.history-range__popover {
  position: absolute;
  z-index: 20;
  top: calc(100% + 0.5rem);
  left: 0;
  width: min(420px, 90vw);
  padding: 1rem;
}
</style>
